import type { AuditLogEntry } from '@core/api/auditLog';
import { ActorType } from '@core/enum/actorType';
import { AuditAction } from '@core/enum/auditAction';
import type { AuditLogRenderer } from '@core/utils/auditLog';
import { toProperCase } from '@core/utils/string';
import {
  ProductionStage,
  productionStageMove,
} from '../../enum/productionStage';
import { HistoryLines } from '../HistoryLines';
import { historySubject, readHistoryColor } from '../historyLabel';

type ProductionStageChangedEntry = AuditLogEntry & {
  action: AuditAction.ProductionStageChanged;
  changes: {
    productionStage: { old: ProductionStage | null; new: ProductionStage };
  };
};

function renderStage(value: ProductionStage | 'none') {
  return value === 'none' ? 'None' : toProperCase(value);
}

export const renderProductionStageChanged: AuditLogRenderer = (entry) => {
  const { changes, actorType } = entry as ProductionStageChangedEntry;
  const stage = changes.productionStage.new;
  const fromStage = changes.productionStage.old;
  const subject = historySubject(entry);
  const color = readHistoryColor(entry);

  if (actorType === ActorType.System && stage === ProductionStage.Queued) {
    return <HistoryLines title={subject} action="Requeued" color={color} />;
  }

  const move = productionStageMove(fromStage, stage);

  if (move === 'next') {
    return (
      <HistoryLines
        title={subject}
        action={<>Moved to {renderStage(stage)}</>}
        color={color}
      />
    );
  }

  if (move === 'back') {
    return (
      <HistoryLines
        title={subject}
        action={
          <>
            Moved back from {renderStage(fromStage ?? 'none')} to{' '}
            {renderStage(stage)}
          </>
        }
        color={color}
      />
    );
  }

  return (
    <HistoryLines
      title={subject}
      action={
        <>
          Moved from {renderStage(fromStage ?? 'none')} to {renderStage(stage)}
        </>
      }
      color={color}
    />
  );
};
